// combined-analytics.service.ts

import { Injectable } from '@angular/core';
import { IAnalyticsService } from './analytics.interface';
import { GTMService } from './gtm.service';
import { OmniSendService } from './omnisend.service';
import { Order, Product, ProductVariant } from '../../../common/generated-types';
import { StateService } from '../state/state.service';
import { combineLatest } from 'rxjs';
import { NGXLogger } from 'ngx-logger';

@Injectable({
    providedIn: 'root'
})
export class CombinedAnalyticsService implements IAnalyticsService {
    constructor(
        private logger: NGXLogger,
        private gtmService: GTMService,
        private omniSendService: OmniSendService,
        private stateService: StateService,
    ) {}

    public async initialize(): Promise<void> {
        const externalServicesSettings$ = this.stateService.select(state => state.externalServicesSettings);
        const shopHost$ = this.stateService.select(state => state.shopHost);
        combineLatest([externalServicesSettings$, shopHost$]).subscribe(([externalServicesSettings, shopHost]) => {
            this.logger.debug('[CombinedAnalyticsService]: initialize', externalServicesSettings, shopHost);
            if (externalServicesSettings) {
                if(externalServicesSettings?.omnisend?.enabled) {
                    const omnisendBrandId = externalServicesSettings?.omnisend?.brandId;
                    if(omnisendBrandId && shopHost) {
                        this.omniSendService.initialize({ omnisendBrandId, shopHost }).then(() => {
                            this.logger.debug('[CombinedAnalyticsService]: omnisend initialized', omnisendBrandId, shopHost);
                        }).catch((error) => {
                            this.logger.error('[CombinedAnalyticsService]: omnisend initialization error', error);
                        });
                    }
                }
                if (externalServicesSettings?.gtag?.enabled) {
                    const gtmId = externalServicesSettings?.gtag?.trackingId;
                    const gtmModeIsServerSide = externalServicesSettings?.gtag?.mode === 'SERVER_SIDE';
                    if(gtmId) {
                        // Initialize GTM without serverContainerUrl
                        // URL rewriting in the proxied script handles redirecting to our endpoints
                        this.gtmService.initialize({ gtmId, gtmModeIsServerSide }).then(() => {
                            this.logger.debug('[CombinedAnalyticsService]: gtag initialized', gtmId, shopHost);
                        }).catch((error) => {
                            this.logger.error('[CombinedAnalyticsService]: gtag initialization error', error);
                        });
                    }
                } else {
                    this.gtmService.disabled();
                }
            }
        });
    }
    public pageView(pageType: string, extra: Record<string, any>): void {
        this.gtmService.pageView(pageType, extra);
    }

    public setEmail(email: string): void {
        this.gtmService.setEmail(email);
        this.omniSendService.setEmail(email);
    }

    public viewItem(product: Product, variant: ProductVariant): void {
        this.gtmService.viewItem(product, variant);
        this.omniSendService.viewItem(product, variant);
    }

    public selectItem(product: Product, variant: ProductVariant): void {
        this.gtmService.selectItem(product, variant);
    }

    public addToCart(product: Product, variant: ProductVariant, quantity: number, order?: Order): void {
        this.gtmService.addToCart(product, variant, quantity);
        if (order) {
            this.omniSendService.addToCart(product, variant, quantity, order);
        }
    }

    public beginCheckout(order: Order, callback?: any): void {
        this.omniSendService.beginCheckout(order);
        this.gtmService.beginCheckout(order, callback);
    }

    public addShippingInfo(order: Order, callback?: any): void {
        this.omniSendService.addShippingInfo(order);
        this.gtmService.addShippingInfo(order, callback);
    }

    public purchase(order: Order, callback?: any): void {
        this.omniSendService.purchase(order);
        this.gtmService.purchase(order, callback);
    }

    public validate_address_button(): void {
        this.omniSendService.validate_address_button();
        this.gtmService.validate_address_button();
    }

    public complete_order_button(): void {
        this.omniSendService.complete_order_button();
        this.gtmService.complete_order_button();
    }
}
